import React, { useState } from 'react';
import { FormattedDate } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { getUserCountryCode } from 'modules/meta/selectors';
import { ButtonLink } from '@daedalus/atlas/components/designSystem/ButtonLink';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { Icon } from '@daedalus/atlas/Icon';
import { Image } from '@daedalus/atlas/Image';
import { Tag } from '@daedalus/atlas/Tag/Tag';
import { imageProvider } from '@daedalus/atlas/testutils/imageProvider';
import { Text } from '@daedalus/atlas/Text';
import { BoardType } from '@daedalus/core/src/api-types/bovio/response/booking';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { getRoomOfferBoardType } from '@daedalus/core/src/offer/business/meal';
import { getRoomAreaWithUnits } from '@daedalus/core/src/room/business/utils/roomAreaUnit';
import { RoomWithoutOffers } from '@daedalus/core/src/room/types/room';
import { RoomDetailsOverlay } from '@daedalus/shared/src/accommodation/RoomDetailsOverlay';
import { createDotSeparatedList } from '@daedalus/shared/src/accommodation/RoomInfo';
import { Meal } from '@daedalus/shared/src/booking/Meal';
import { MirroredIcon } from '@daedalus/shared/src/booking/MirroredIcon';
interface RoomDetailsInfoProps {
  room: RoomWithoutOffers;
  date: string;
  midDate: string;
  isCheckInDateAvailable?: boolean;
  services: string[];
  placement?: string;
  showImage?: boolean;
}
const _exp = /*#__PURE__*/() => ({
  isMobile
}) => isMobile ? 80 : 158;
const ImageWrapper = /*#__PURE__*/styled('div')({
  name: "ImageWrapper",
  class: "iap3bu5",
  propsAsIs: false,
  vars: {
    "iap3bu5-0": [_exp(), "px"]
  }
});
const _exp4 = /*#__PURE__*/() => Text;
const RoomCharacteristicItem = /*#__PURE__*/styled(_exp4())({
  name: "RoomCharacteristicItem",
  class: "r1dfrvg2",
  propsAsIs: true
});
const _exp6 = /*#__PURE__*/() => Grid;
const RoomDetailsInfoWrapper = /*#__PURE__*/styled(_exp6())({
  name: "RoomDetailsInfoWrapper",
  class: "ruacci9",
  propsAsIs: true
});
const roomNameStyles = "rjt37p4";
const _exp9 = /*#__PURE__*/() => ContentWrapper;
const MainContentWrapper = /*#__PURE__*/styled(_exp9())({
  name: "MainContentWrapper",
  class: "m1v76g7y",
  propsAsIs: true
});
const RoomOptionsWrapper = /*#__PURE__*/styled('div')({
  name: "RoomOptionsWrapper",
  class: "ryoo5hf",
  propsAsIs: false
});
const _exp12 = /*#__PURE__*/() => ({
  isMobile
}) => isMobile ? 80 : 158;
const NoRoomImagePlaceholder = /*#__PURE__*/styled('div')({
  name: "NoRoomImagePlaceholder",
  class: "ngr4xcu",
  propsAsIs: false,
  vars: {
    "ngr4xcu-0": [_exp12(), "px"]
  }
});
const _exp13 = /*#__PURE__*/() => Text;
const RoomInformationItemWrapper = /*#__PURE__*/styled(_exp13())({
  name: "RoomInformationItemWrapper",
  class: "ruhzrww",
  propsAsIs: true
});
const _exp14 = /*#__PURE__*/() => Icon;
const StyledIcon = /*#__PURE__*/styled(_exp14())({
  name: "StyledIcon",
  class: "s8h5mk4",
  propsAsIs: true
});
export const RoomDetailsInfo = ({
  room,
  date,
  midDate,
  isCheckInDateAvailable = false,
  services,
  placement,
  showImage = true
}: RoomDetailsInfoProps) => {
  const {
    isMobile
  } = useDeviceLayout();
  const [isRoomDetailsOverlayOpen, setIsRoomDetailsOverlayOpen] = useState(false);
  const boardType = getRoomOfferBoardType(services);
  const userCountry = useSelector(getUserCountryCode);
  const roomArea = getRoomAreaWithUnits(room.area, userCountry);
  const openRoomDetailsOverlay = () => {
    setIsRoomDetailsOverlayOpen(true);
  };
  const enableRoomDetails = room && (room?.images?.length > 0 || room?.description?.length > 0 || room?.amenities?.length > 0);
  const bedNames = room.bedTypes.filter(bed => bed.name).map(bed => bed.name.trim());
  const roomAreaAndBedNames = [...bedNames, ...(roomArea ? [roomArea] : [])];
  return <>
      <RoomDetailsInfoWrapper container spacing="s000">
        {showImage && <ImageWrapper isMobile={isMobile} onClick={openRoomDetailsOverlay}>
            {room.images.length === 0 ? <NoRoomImagePlaceholder isMobile={isMobile}>
                <Icon name="PhotoError" colorPath="content.neutral.c400" size="lg" />
              </NoRoomImagePlaceholder> : <Image url={room.images[0].url} />}
          </ImageWrapper>}
        <MainContentWrapper paddingLeft="s400">
          <ContentWrapper paddingY="s400" paddingLeft="s000" paddingRight="s400">
            <ContentWrapper paddingBottom="s250">
              <Grid container spacing="s000" alignItems="center" justify="space-between">
                <Tag variant="neutral" size="sm">
                  <Grid container alignItems="center" spacing="s000" justify="center">
                    <Text variant="labelXS" as="span" dataId={`splitBooking.checkIn.${placement}`}>
                      <FormattedDate value={isCheckInDateAvailable ? date : midDate} day="numeric" month="short" timeZone="UTC" />
                    </Text>
                    <ContentWrapper paddingX="s100">
                      <MirroredIcon name="ArrowRight" size="xs" colorPath="content.neutral.c600" />
                    </ContentWrapper>
                    <Text variant="labelXS" as="span" dataId={`splitBooking.checkOut.${placement}`}>
                      <FormattedDate value={isCheckInDateAvailable ? midDate : date} day="numeric" month="short" timeZone="UTC" />
                    </Text>
                  </Grid>
                </Tag>
                {enableRoomDetails ? <ButtonLink dataId={`roomDetailsButton.${placement}`} size="sm" variant="default" isCompact onClick={openRoomDetailsOverlay}>
                    <Text variant={isMobile ? 'labelXS' : 'labelS'}>
                      {isMobile ? <FormattedMessageWrapper id="details" defaultMessage="Details" ddActionName="RoomDetailsButton" /> : <FormattedMessageWrapper id="roomDetails" defaultMessage="Room details" ddActionName="RoomDetailsButton" />}
                    </Text>
                  </ButtonLink> : null}
              </Grid>
            </ContentWrapper>

            <Text variant="titleM" colorPath="content.neutral.c950" className={roomNameStyles}>
              {room.name}
            </Text>
            {isMobile ? <RoomCharacteristicItem variant="bodyS" colorPath="content.neutral.c600">
                {createDotSeparatedList(roomAreaAndBedNames)}
              </RoomCharacteristicItem> : <Flex.Row gap="s500">
                {bedNames.length > 0 && <RoomInformationItemWrapper variant="bodyS" colorPath="content.neutral.c600">
                    <StyledIcon name="DoubleBed" size="sm" />
                    {createDotSeparatedList(bedNames)}
                  </RoomInformationItemWrapper>}
                {roomArea && <RoomInformationItemWrapper variant="bodyS" colorPath="content.neutral.c600">
                    <StyledIcon name="Maximize2" size="sm" />
                    {roomArea}
                  </RoomInformationItemWrapper>}
              </Flex.Row>}
            <RoomOptionsWrapper>
              {boardType && boardType !== BoardType.RoomOnly && <Grid container alignItems="center" spacing="s000" gap="s250">
                  <Meal boardType={boardType} />
                </Grid>}
            </RoomOptionsWrapper>
          </ContentWrapper>
        </MainContentWrapper>
      </RoomDetailsInfoWrapper>
      {enableRoomDetails && <RoomDetailsOverlay room={room} imageProvider={imageProvider} isOpen={isRoomDetailsOverlayOpen} onClose={() => setIsRoomDetailsOverlayOpen(false)} userCountry={userCountry} />}
    </>;
};

require("./RoomDetailsInfo.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomDetailsInfo.tsx");